import React, { useContext } from "react"
import { UIContext } from "../shared/layout"
import styled from "styled-components"
import Breakpoints from "../shared/breakpoints"
import Grid from "../shared/grid"
import ButtonLink from "../shared/link"
import { T } from "../shared/typography.styles"
import { useStaticQuery, graphql } from "gatsby"
import Slider from "react-slick"
import ExternalLinks from "../../external-links"
import FeatureCard from "../../design-system/molecules/resource-card"
import { Block, Spacer } from "../../design-system/atoms/layout-primitives"

const PostContainer = styled(Block)`
  display: flex;
  flex-direction: column;

  & > * {
    box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.07);
  }

  & > *:not(:last-child) {
    margin-bottom: 24px;
  }

  .slick-dots {
    bottom: -40px;
  }

  @media (min-width: ${Breakpoints.lg}) {
    flex-wrap: nowrap;
    flex-direction: row;
    align-items: flex-start;
    justify-content: center;
    flex-wrap: nowrap;

    & > * {
      flex: 1 1 33%;
      &,
      &:not(:last-child) {
        margin: 16px;
      }
    }
  }
`

const Container = styled.div`
  border-bottom: 1px solid #26262626;
  padding: 64px 0 48px;
  @media (min-width: ${Breakpoints.md}) {
    padding: 64px 0;
  }
  @media (min-width: ${Breakpoints.lg}) {
    padding: 128px 0 96px;
  }
`

const Title = styled.h2`
  ${T.sm.h2.bold}
  grid-column: 1 / -1;
  text-align: center;
  color: var(--slate);
  @media (min-width: ${Breakpoints.md}) {
    ${T.md.h2.bold}
  }
  @media (min-width: ${Breakpoints.lg}) {
    ${T.lg.h2.bold}
    grid-column: 2/-2;
  }
`

const BlogCardLayoutContainer = styled.div`
  grid-column: 1 / -1;
  margin: 48px 0 32px;
  @media (min-width: ${Breakpoints.md}) {
    grid-column: 2 / -2;
    margin: 64px 0 32px;
  }
  @media (min-width: ${Breakpoints.lg}) {
    margin-bottom: 0;
    box-shadow: none;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-gap: 32px;
    display: grid;
    grid-column: 2 / -2;
    align-items: start;
  }
`

const ReadBlogButton = styled(ButtonLink)`
  grid-column: 1/-1;
  grid-column: 2/-2;
  width: max-content;
  justify-self: center;
  padding: 16px 48px;
  @media (min-width: ${Breakpoints.md}) {
    grid-column: 5/-5;
  }
`

export default function ContentPromoSection(props) {
  const query = useStaticQuery(graphql`
    query {
      blogSDK: file(
        relativePath: {
          eq: "3_RESOURCES/Partner Program/01_desktop/blog_image_1.jpg"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 1440) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      blogThoughtwire: file(
        relativePath: {
          eq: "3_RESOURCES/Partner Program/01_desktop/blog_image_2.jpg"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 1440) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      blogServiceNow: file(
        relativePath: {
          eq: "3_RESOURCES/Partner Program/01_desktop/blog_image_3.jpeg"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 1440) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
    }
  `)

  const blogCardDetails = [
    {
      fluidImage: query.blogServiceNow.childImageSharp.fluid,
      title: "ServiceNow And Mappedin: Improving the Employee Experience",
      resourceName: "Blog",
      description:
        "Mappedin provides indoor mapping capabilities within ServiceNow’s Workplace Service Delivery solution. What does this integration look like?",
      link: ExternalLinks.hubspot.partnerServiceNowBlog,
    },
    {
      fluidImage: query.blogSDK.childImageSharp.fluid,
      title: "Partner Applications of Mappedin SDKs",
      resourceName: "Blog",
      description:
        "Mappedin's partners are able to create powerful, accurate, and completely custom experiences using our Mapping SDKs. Here are some of the ways how.",
      link: ExternalLinks.hubspot.partnerSdkBlog,
    },
    {
      fluidImage: query.blogThoughtwire.childImageSharp.fluid,
      title:
        "Thoughtwire-Mappedin Partnership Provides IOT-Informed Mapping For Built Environments",
      resourceName: "NewsRoom",
      description:
        "ThoughtWire, the leader in Operations Performance Management (OPM) for built environments, announces its partnership with Mappedin.",
      link: ExternalLinks.hubspot.partnerThoughtWireBlog,
    },
  ]

  const {
    state: { breakpoint, clientLoaded },
  } = useContext(UIContext)

  return (
    <>
      <Container>
        <Grid>
          <Title>Learn more about partnering with us</Title>
          <Spacer height={{ sm: "48px", md: "64px", lg: "96px" }} />
          <PostContainer col={{ sm: "1 / -1", md: "3 / -3", lg: "1 / -1" }}>
            {breakpoint === "lg"
              ? blogCardDetails.map((blog) => (
                  <FeatureCard
                    fluid={blog.fluidImage}
                    resourceName={blog.resourceName}
                    title={blog.title}
                    description={blog.description}
                    link={blog.link}
                  />
                ))
              : clientLoaded && (
                  <Slider dots arrows>
                    {blogCardDetails.map((blog) => (
                      <FeatureCard
                        fluid={blog.fluidImage}
                        resourceName={blog.resourceName}
                        title={blog.title}
                        description={blog.description}
                        link={blog.link}
                      />
                    ))}
                  </Slider>
                )}
          </PostContainer>
          <Spacer height={{ sm: "64px", md: "64px", lg: "48px" }} />
          <ReadBlogButton href={ExternalLinks.hubspot.blog}>
            Read our Blog
          </ReadBlogButton>
        </Grid>
      </Container>
    </>
  )
}

{
  /* 
    <BlogCardLayoutContainer>
      {breakpoint === "lg" ? (
        <>
          {blogCards
            .sort((a, b) => a.desktopIndex - b.desktopIndex)
            .map((list) => list.component)}
        </>
      ) : (
        clientLoaded && (
          <Slider dots arrows>
            {blogCards
              .sort((a, b) => a.mobileIndex - b.mobileIndex)
              .map((list) => list.component)}
          </Slider>
        )
      )}
    </BlogCardLayoutContainer>
            */
}

/*
export default function ContentPromoSection() {
  return (
    <Container>
      <Grid>
        <Title>Learn more about partnering with us</Title>
        <BlogCardLayout />
        <ReadBlogButton href={externalLinks.hubspot.blog}>
          Read our Blog
        </ReadBlogButton>
      </Grid>
    </Container>
  )
}
*/
